import axios from "axios";
import { IPublicClientApplication } from "@azure/msal-browser";
import { apiRequest } from "../authConfig";

const API_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

export interface Batch {
  batchId: string;
  orderDate: string;
  issueCount: number;
  warehouse: string;
  branch: string;
  status: string;
  colorTag: string | null;
  noteCount: number;
}

export interface PaginatedBatch {
  totalCount: number;
  page: number;
  pageSize: number;
  data: Batch[];
}

export interface ReconciliationDetail {
  inventoryId: string;
  vendorId: string;
  freightVendorId: string;
  receivedQtyCover: number;
  receivedQtyScanned: number;
  receivedQtyVariance: number;
  countCover: number;
  countScanned: number;
  countVariance: number;
  status: string;
}

export interface Reconciliation {
  batchId: string;
  data: ReconciliationDetail[];
}

export const getBatches = async (
  instance: IPublicClientApplication,
  account: any,
  page: number = 1,
  filter?: string,
  statusFilter?: string | null,
  colorFilter?: string | null,
  startDate?: string | null,
  endDate?: string | null,
  presetBranches?: string[] | null,
  presetWarehouses?: string[] | null,
  issueFilter?: string | null,
): Promise<PaginatedBatch> => {
  const response = await instance.acquireTokenSilent({
    ...apiRequest,
    account: account,
  });
  const config = {
    headers: { Authorization: `Bearer ${response.accessToken}` },
    params: {
      page,
      filter,
      statusFilter: statusFilter || undefined,
      colorFilter: colorFilter || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      presetBranches: presetBranches && presetBranches.length > 0 ? presetBranches.join(",") : undefined,
      presetWarehouses: presetWarehouses && presetWarehouses.length > 0 ? presetWarehouses.join(",") : undefined,
      issueFilter: issueFilter || undefined,
    },
  };
  try {
    const apiResponse = await axios.get<PaginatedBatch>(
      `${API_URL}/batch`,
      config,
    );
    return apiResponse.data;
  } catch (error) {
    console.error(`Error fetching tickets:`, error);
    throw error;
  }
};

export const getbatch = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
): Promise<Batch> => {
  const response = await instance.acquireTokenSilent({
    ...apiRequest,
    account: account,
  });
  const config = {
    headers: { Authorization: `Bearer ${response.accessToken}` },
  };
  try {
    const apiResponse = await axios.get<Batch>(
      `${API_URL}/batch/${batchId}`,
      config,
    );
    return apiResponse.data;
  } catch (error) {
    console.error(`Error fetching batch:`, error);
    throw error;
  }
};

export const getReconciliation = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
): Promise<Reconciliation> => {
  const response = await instance.acquireTokenSilent({
    ...apiRequest,
    account: account,
  });
  const config = {
    headers: { Authorization: `Bearer ${response.accessToken}` },
  };
  try {
    const apiResponse = await axios.get<Reconciliation>(
      `${API_URL}/batch/reconcile/${batchId}`,
      config,
    );
    return apiResponse.data;
  } catch (error) {
    console.error(`Error fetching batch:`, error);
    throw error;
  }
};

export const updateBatchDate = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
  orderDate: string,
): Promise<void> => {
  const response = await instance.acquireTokenSilent({
    ...apiRequest,
    account: account,
  });
  const config = {
    headers: { Authorization: `Bearer ${response.accessToken}` },
  };
  try {
    await axios.patch(`${API_URL}/batch/${batchId}/date`, { orderDate }, config);
  } catch (error) {
    console.error(`Error updating batch date:`, error);
    throw error;
  }
};

export const markBatchComplete = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
): Promise<void> => {
  const response = await instance.acquireTokenSilent({
    ...apiRequest,
    account: account,
  });
  const config = {
    headers: { Authorization: `Bearer ${response.accessToken}` },
  };
  try {
    await axios.patch(`${API_URL}/batch/${batchId}/complete`, {}, config);
  } catch (error) {
    console.error(`Error marking batch complete:`, error);
    throw error;
  }
};

export const updateBatchColor = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
  colorTag: string | null,
): Promise<void> => {
  const response = await instance.acquireTokenSilent({
    ...apiRequest,
    account: account,
  });
  const config = {
    headers: { Authorization: `Bearer ${response.accessToken}` },
  };
  try {
    await axios.patch(`${API_URL}/batch/${batchId}/color`, { colorTag }, config);
  } catch (error) {
    console.error(`Error updating batch color:`, error);
    throw error;
  }
};

export const deleteBatch = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
): Promise<void> => {
  const response = await instance.acquireTokenSilent({
    ...apiRequest,
    account: account,
  });
  const config = {
    headers: { Authorization: `Bearer ${response.accessToken}` },
  };
    try {
    const apiResponse = await axios.delete<void>(
      `${API_URL}/batch/${batchId}`,
      config,
    );
    return apiResponse.data;
  } catch (error) {
    console.error(`Error deleting batch:`, error);
    throw error;
  }
};

export interface MaterialCountRow {
  inventoryId: string;
  warehouse: string | null;
  branch: string | null;
  countCover: number;
  countScanned: number;
  countVariance: number;
  status: string;
}

export interface MaterialQtyRow {
  inventoryId: string;
  warehouse: string | null;
  branch: string | null;
  receivedQtyCover: number;
  receivedQtyScanned: number;
  receivedQtyVariance: number;
  status: string;
}

export interface SupplierRow {
  vendorId: string;
  warehouse: string | null;
  branch: string | null;
  countCover: number;
  countScanned: number;
  countVariance: number;
  status: string;
}

export interface HaulerRow {
  freightVendorId: string;
  warehouse: string | null;
  branch: string | null;
  countCover: number;
  countScanned: number;
  countVariance: number;
  status: string;
}

export interface ReconcileResult<T> {
  batchId: string;
  data: T[];
}

const makeAuthConfig = async (instance: IPublicClientApplication, account: any) => {
  const response = await instance.acquireTokenSilent({ ...apiRequest, account });
  return { headers: { Authorization: `Bearer ${response.accessToken}` } };
};

export const reconcileBatch = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
): Promise<void> => {
  try {
    const config = await makeAuthConfig(instance, account);
    await axios.post(`${API_URL}/batch/reconcile/${batchId}`, {}, config);
  } catch (error) {
    console.error('Error triggering batch reconciliation:', error);
    throw error;
  }
};

export const getMaterialCountReconciliation = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
): Promise<ReconcileResult<MaterialCountRow>> => {
  try {
    const config = await makeAuthConfig(instance, account);
    const res = await axios.get<ReconcileResult<MaterialCountRow>>(`${API_URL}/batch/reconcile/${batchId}/material-count`, config);
    return res.data;
  } catch (error) {
    console.error('Error fetching material count reconciliation:', error);
    throw error;
  }
};

export const getMaterialQtyReconciliation = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
): Promise<ReconcileResult<MaterialQtyRow>> => {
  try {
    const config = await makeAuthConfig(instance, account);
    const res = await axios.get<ReconcileResult<MaterialQtyRow>>(`${API_URL}/batch/reconcile/${batchId}/material-qty`, config);
    return res.data;
  } catch (error) {
    console.error('Error fetching material qty reconciliation:', error);
    throw error;
  }
};

export const getSupplierReconciliation = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
): Promise<ReconcileResult<SupplierRow>> => {
  try {
    const config = await makeAuthConfig(instance, account);
    const res = await axios.get<ReconcileResult<SupplierRow>>(`${API_URL}/batch/reconcile/${batchId}/supplier`, config);
    return res.data;
  } catch (error) {
    console.error('Error fetching supplier reconciliation:', error);
    throw error;
  }
};

export const getHaulerReconciliation = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
): Promise<ReconcileResult<HaulerRow>> => {
  try {
    const config = await makeAuthConfig(instance, account);
    const res = await axios.get<ReconcileResult<HaulerRow>>(`${API_URL}/batch/reconcile/${batchId}/hauler`, config);
    return res.data;
  } catch (error) {
    console.error('Error fetching hauler reconciliation:', error);
    throw error;
  }
};

export interface BatchProgress {
  batchId: string;
  processed: number;
  pending: number;
  total: number;
  isProcessing: boolean;
}

const authConfig = async (instance: IPublicClientApplication, account: any) => {
  const response = await instance.acquireTokenSilent({ ...apiRequest, account });
  return { headers: { Authorization: `Bearer ${response.accessToken}` } };
};

/** Batches with pages still waiting in scanspending or currently processing. */
export const getUnfinishedBatches = async (
  instance: IPublicClientApplication,
  account: any,
): Promise<BatchProgress[]> => {
  const res = await axios.get<BatchProgress[]>(`${API_URL}/batch/processing`, await authConfig(instance, account));
  return res.data;
};

export const getBatchProgress = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
): Promise<BatchProgress> => {
  const res = await axios.get<BatchProgress>(`${API_URL}/batch/${batchId}/processing`, await authConfig(instance, account));
  return res.data;
};

/** Admin: re-run pages stuck in scanspending. */
export const retryPendingPages = async (
  instance: IPublicClientApplication,
  account: any,
  batchId: string,
): Promise<BatchProgress> => {
  const res = await axios.post<BatchProgress>(`${API_URL}/batch/${batchId}/retry-pending`, {}, await authConfig(instance, account));
  return res.data;
};
